import React, { useMemo, useState } from "react";
import Video from "./Video.jsx";

const Carousel = ({ items = [], visibleCount = 3, onSelect, renderItem }) => {
  const [startIndex, setStartIndex] = useState(0);
  const len = items.length;

  const canScroll = len > visibleCount;

  const visibleItems = useMemo(() => {
    if (len === 0) return [];
    const out = [];
    for (let i = 0; i < Math.min(visibleCount, len); i += 1) {
      out.push(items[(startIndex + i) % len]);
    }
    return out;
  }, [items, len, startIndex, visibleCount]);

  const prev = () => {
    if (!canScroll) return;
    setStartIndex((idx) => (idx - 1 + len) % len);
  };

  const next = () => {
    if (!canScroll) return;
    setStartIndex((idx) => (idx + 1) % len);
  };

  if (items.length <= 3) {
    return (
      <div className="w-full overflow-hidden">
        <div className="flex items-center gap-3 my-8">
          <div className="flex-1">
            <div className="flex justify-center gap-8 flex-col md:flex-row">
              {visibleItems.map((item, i) => (
                <div
                  key={`item-${i}`}
                  onClick={() => onSelect && onSelect(item, (startIndex + i) % (len || 1))}
                  className="group text-center"
                >
                  <div className="aspect-video w-full md:basis-1/3 shrink-0 min-w-0 h-64 md:h-48 lg:h-64">
                    <Video title={item.title} src={item.videoUrl} framed={false} className="h-full w-full" />
                  </div>
                  {item?.title && (
                    <div className="sr-only">
                      {item.title}
                    </div>
                  )}
                </div>
              ))}
              {len === 0 && <div className="col-span-full text-center text-sm text-gray-500">No videos</div>}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full">
      <div className="flex items-center gap-3 my-8 mx-4 h-64">
        {/* Left control */}
        <button
          type="button"
          onClick={prev}
          disabled={!canScroll}
          aria-label="Previous"
          className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40`}
        >
          <span className="sr-only">Previous</span>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M15 6L9 12L15 18"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>

        {/* Items */}
        <div className="flex-1">
          <div className="flex justify-center gap-4">
            {visibleItems.map((item, i) => (
              <button
                key={`item-${i}`}
                type="button"
                onClick={() => onSelect && onSelect(item, (startIndex + i) % (len || 1))}
                className="group text-center"
              >
                <div className="aspect-video w-full overflow-hidden basis-1/3 shrink-0 min-w-0">
                  {renderItem ? (
                    renderItem(item)
                  ) : item?.videoUrl ? (
                    <Video title={item.title} src={item.videoUrl} framed={false} className="h-full w-full" />
                  ) : item?.thumbnail ? (
                    <img src={item.thumbnail} alt={item.title || ""} className="h-full w-full object-cover" />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center text-sm text-gray-400">Video</div>
                  )}
                </div>
                {item?.title && (
                  <div className="text-proof-blue mt-2 line-clamp-1 text-sm font-medium group-hover:underline">
                    {item.title}
                  </div>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Right control */}
        <button
          type="button"
          onClick={next}
          disabled={!canScroll}
          aria-label="Next"
          className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40`}
        >
          <span className="sr-only">Next</span>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M9 6L15 12L9 18"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default Carousel;
